import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { App, Button, Empty, Input, Modal, Slider, Space, Spin, Switch, Tooltip, Tag, Typography, Upload } from 'antd';
import {
  LeftOutlined,
  DeleteOutlined,
  EditOutlined,
  CheckCircleOutlined,
  BookOutlined,
  AppstoreOutlined,
  ExpandOutlined,
  PictureOutlined,
  UndoOutlined,
  RedoOutlined,
} from '@ant-design/icons';
import { setHostContainerLarge } from '../api/bitable';
import { FullscreenOverlay } from '../fullscreen/FullscreenOverlay';
import type { FieldMeta } from '../api/types';
import { useAppDispatch, useAppState } from '../store/hooks';
import { useContractTemplates } from '../contract/useContractTemplates';
import { useTemplates } from '../store/useTemplates';
import { NumberedSection } from '../components/shell/NumberedSection';
import { loadUnderlay, saveUnderlay, type Underlay } from '../utils/underlay';
import { applyCellPatch, applyCellText, applySectionText, applyTableOp, convertSection, createBlock, duplicateSection, insertAfter, rebindField, type TableOpMessage } from '../contract/canvasOps';
import { INSERTABLE_SECTION_TYPES, SECTION_TYPE_LABELS, type ContractSectionType } from '../contract/types';
import { collectBindings } from '../../render-core/bindings';
import { FieldPanel } from '../components/FieldPanel';
import { PrintSettings } from '../components/PrintSettings';
import { SectionEditor } from '../components/ContractMode/SectionEditor';
import { ContractSettings } from '../components/ContractMode/ContractSettings';
import { ContractPreview } from '../components/ContractMode/ContractPreview';
import { CanvasHints, EditorGuide, useEditorGuide } from '../components/shell/EditorGuide';
import { useTemplateHistory } from '../contract/useHistory';
import { ShareToggle } from '../components/shell/ShareToggle';
import { launchFullscreen } from '../fullscreen/launch';
import { CellStylePanel } from '../components/ContractMode/editors/CellStylePanel';
import {
  findSectionDeep,
  insertAtDeep,
  moveSectionDeep,
  removeSectionDeep,
  reorderDeep,
} from '../../render-core/sectionTree';
import type { CellProps, Rect } from '../contract/freeTable';
import type { FreeTableSection } from '../contract/types';
import { OverlaysEditor } from '../components/ContractMode/editors/OverlaysEditor';
import { PreviewPane } from '../components/PreviewPane';
import type { OverlayItem } from '../contract/types';

interface Props {
  fieldMetas: FieldMeta[];
  visibleFieldIds: string[];
  bootstrapLoading: boolean;
}

export function EditorPage({ fieldMetas, visibleFieldIds, bootstrapLoading }: Props) {
  const { message } = App.useApp();
  const state = useAppState();
  const dispatch = useAppDispatch();
  const contractTpl = useContractTemplates(state.context?.tableId ?? null);
  const tableTpl = useTemplates(state.context?.tableId ?? null);
  const isContract = state.mode === 'contract';

  const editingId = state.editingTemplateId;

  const contractTemplate = useMemo(
    () => (isContract && editingId
      ? contractTpl.templates.find((t) => t.id === editingId)
      : undefined),
    [isContract, editingId, contractTpl.templates],
  );
  const tableTemplate = useMemo(
    () => (!isContract && editingId
      ? tableTpl.templates.find((t) => t.id === editingId)
      : undefined),
    [isContract, editingId, tableTpl.templates],
  );

  const name = isContract ? contractTemplate?.name : tableTemplate?.name;
  const hasTemplate = isContract ? !!contractTemplate : !!tableTemplate;

  // ── 视图模板：进来先应用，改完写回 ──────────────────────────
  //
  // 编辑器里的字段面板和排版面板改的是全局 state（画布靠它渲染），跟模板本身
  // 是两份数据。以前只有前者，于是「自动保存」是假的：退出再进来，模板还是
  // 建的时候那份快照，切换模板也毫无变化。
  const appliedTemplateRef = useRef<string | null>(null);
  useEffect(() => {
    if (isContract || !tableTemplate) {
      appliedTemplateRef.current = null;
      return;
    }
    if (appliedTemplateRef.current === tableTemplate.id) return;
    appliedTemplateRef.current = tableTemplate.id;
    dispatch({
      type: 'APPLY_TEMPLATE',
      fieldIds: tableTemplate.fieldIds,
      settings: tableTemplate.settings,
    });
  }, [isContract, tableTemplate, dispatch]);

  // 应用完成之后，字段勾选/排序/排版的每次改动都写回模板
  const checkedFieldIds = useMemo(
    () =>
      state.fieldOptions
        .filter((f) => f.checked)
        .slice()
        .sort((a, b) => a.order - b.order)
        .map((f) => f.id),
    [state.fieldOptions],
  );
  useEffect(() => {
    if (isContract || !editingId || !tableTemplate) return;
    if (appliedTemplateRef.current !== tableTemplate.id) return;
    // 字段列表还没加载出来时别写回，否则会把模板的字段清空
    if (state.fieldOptions.length === 0) return;
    tableTpl.update(editingId, { fieldIds: checkedFieldIds, settings: state.settings });
    // tableTpl 每次渲染都是新对象，放进依赖会自激；这里只关心内容变化
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isContract, editingId, tableTemplate?.id, checkedFieldIds, state.settings, state.fieldOptions.length]);

  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState(name || '');
  useEffect(() => setNameDraft(name || ''), [name]);

  // 画布 ↔ 区块面板 双向联动的选中态
  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(null);
  // 用户主动折叠过就别再自动弹开。以前只要 selectedSectionId 变 null 就兜底
  // 选第一个 —— 于是折叠第 3 个区块，第 1 个自己展开并把页面滚上去，
  // 用户永远收不起全部面板。
  const collapsedByUserRef = useRef(false);
  useEffect(() => {
    if (!contractTemplate) return;
    if (selectedSectionId && findSectionDeep(contractTemplate.sections, selectedSectionId)) return;
    if (selectedSectionId === null && collapsedByUserRef.current) return;
    setSelectedSectionId(contractTemplate.sections[0]?.id ?? null);
  }, [contractTemplate, selectedSectionId]);

  const setActiveSection = (id: string | null) => {
    collapsedByUserRef.current = id === null;
    setSelectedSectionId(id);
  };

  // 字段绑定体检：模板里按名字绑的引用（模板库/AI/{{占位符}}）在字段改名或
  // 删除后会静默落空，打印出来就是一格空白，界面上一点提示都没有。
  const [showUnmatched, setShowUnmatched] = useState(false);
  const bindingReport = useMemo(
    () => (contractTemplate ? collectBindings(contractTemplate, fieldMetas) : null),
    [contractTemplate, fieldMetas],
  );

  const [locate, setLocate] = useState<{ id: string; n: number } | null>(null);
  const [cellSel, setCellSel] = useState<({ id: string } & Rect) | null>(null);
  const selectFromCanvas = (id: string) => {
    collapsedByUserRef.current = false;
    setSelectedSectionId(id);
    if (cellSel && cellSel.id !== id) setCellSel(null);
    // 面板展开后滚动到该区块编辑器
    setTimeout(() => {
      document.getElementById(`pp-panel-${id}`)?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }, 120);
  };

  const moveSectionById = (id: string, dir: -1 | 1) => {
    if (!editingId || !contractTemplate) return;
    contractTpl.update(editingId, { sections: moveSectionDeep(contractTemplate.sections, id, dir) });
  };

  /** 画布上双击改完文字 —— 直接写回区块的 content，不用去下面的面板里找。 */
  const setSectionTextById = (id: string, text: string) => {
    if (!editingId || !contractTemplate) return;
    contractTpl.update(editingId, { sections: applySectionText(contractTemplate.sections, id, text) });
  };

  // ── 对照底图：垫一张纸质单据的照片在画布下面，照着它排 ──
  const [underlay, setUnderlay] = useState<Underlay | null>(null);
  useEffect(() => {
    setUnderlay(editingId ? loadUnderlay(editingId) : null);
  }, [editingId]);
  const putUnderlay = (u: Underlay | null) => {
    if (!editingId) return;
    setUnderlay(u);
    if (!saveUnderlay(editingId, u)) {
      message.error('底图没能存进本机：浏览器存储空间不足，试试压缩图片');
    }
  };
  const pickUnderlay = (file: File): boolean => {
    // 8MB 以上的扫描件本机存不下，先拦住而不是等 localStorage 抛错
    if (file.size > 8 * 1024 * 1024) {
      message.error('底图超过 8MB，请先压缩一下');
      return false;
    }
    const r = new FileReader();
    r.onload = () =>
      putUnderlay({ dataUrl: String(r.result || ''), opacity: underlay?.opacity ?? 0.35, visible: true });
    r.onerror = () => message.error('读取图片失败');
    r.readAsDataURL(file);
    return false;
  };

  // ── 画布动作 → 模板改动（语义统一在 contract/canvasOps） ──
  const onTableOp = (msg: TableOpMessage) => {
    if (!editingId || !contractTemplate) return;
    contractTpl.update(editingId, { sections: applyTableOp(contractTemplate.sections, msg) });
  };
  const onCellText = (id: string, r: number, c: number, text: string) => {
    if (!editingId || !contractTemplate) return;
    contractTpl.update(editingId, { sections: applyCellText(contractTemplate.sections, id, r, c, text) });
  };
  const onInsertAfter = (afterId: string, blockType: string, text?: string) => {
    if (!editingId || !contractTemplate) return;
    const block = createBlock(blockType);
    if (!block) return;
    if (text && block.type === 'text_block') (block as { content: string }).content = text;
    const { sections, id } = insertAfter(contractTemplate.sections, afterId, block);
    contractTpl.update(editingId, { sections });
    setSelectedSectionId(id);
  };

  const onCellPatch = (patch: Partial<CellProps>) => {
    if (!editingId || !contractTemplate || !cellSel) return;
    contractTpl.update(editingId, { sections: applyCellPatch(contractTemplate.sections, cellSel.id, cellSel, patch) });
  };
  const onSectionDuplicate = (id: string) => {
    if (!editingId || !contractTemplate) return;
    const { sections, id: newId } = duplicateSection(contractTemplate.sections, id);
    contractTpl.update(editingId, { sections });
    if (newId) setSelectedSectionId(newId);
  };
  const onRebind = (id: string, from: string, to: string) => {
    if (!editingId || !contractTemplate) return;
    contractTpl.update(editingId, { sections: rebindField(contractTemplate.sections, id, from, to, fieldMetas) });
  };
  const onSectionConvert = (id: string, blockType: string) => {
    if (!editingId || !contractTemplate) return;
    contractTpl.update(editingId, { sections: convertSection(contractTemplate.sections, id, blockType) });
  };

  /** 画布上拖动手柄换顺序：把一个区块挪到目标区块的前面或后面。 */
  const reorderSection = (id: string, targetId: string, before: boolean) => {
    if (!editingId || !contractTemplate || id === targetId) return;
    contractTpl.update(editingId, { sections: reorderDeep(contractTemplate.sections, id, targetId, before) });
    setSelectedSectionId(id);
  };

  /** 画布上点了区块之间的「+」：选好类型后插在那个位置。 */
  const [insertAt, setInsertAt] = useState<{ index: number; parent: { id: string; col: number } | null } | null>(null);
  const insertSectionAt = (type: ContractSectionType) => {
    if (!editingId || !contractTemplate || insertAt === null) return;
    const block = createBlock(type);
    if (!block) return;
    const sections = insertAtDeep(contractTemplate.sections, block, insertAt.index, insertAt.parent?.id, insertAt.parent?.col);
    contractTpl.update(editingId, { sections });
    setInsertAt(null);
    setSelectedSectionId(block.id);
  };

  const deleteSectionById = (id: string) => {
    if (!editingId || !contractTemplate) return;
    contractTpl.update(editingId, { sections: removeSectionDeep(contractTemplate.sections, id) });
  };

  // 全屏编辑有两种形态：
  //   独立标签页（首选）—— 侧边栏被飞书挤在一条窄栏里，三栏编辑器根本摊不开，
  //     整页才够用。没有 SDK，所以先把字段和当前记录的渲染数据存一份快照，
  //     模板走云同步双向。
  //   插件内浮层（兜底）—— 浏览器拦了弹窗时退回它，总比什么都不发生强。
  const guide = useEditorGuide();
  const applyHistory = useCallback(
    (patch: Parameters<typeof contractTpl.update>[1]) => {
      if (editingId) contractTpl.update(editingId, patch);
    },
    [editingId, contractTpl],
  );
  const history = useTemplateHistory(contractTemplate ?? null, applyHistory);
  const [fullscreen, setFullscreen] = useState(false);
  const [openingTab, setOpeningTab] = useState(false);
  const openInPanel = () => {
    setFullscreen(true);
    void setHostContainerLarge(true);
  };
  const openFullscreen = async () => {
    if (!editingId || !contractTemplate) return;
    setOpeningTab(true);
    try {
      const r = await launchFullscreen({
        template: contractTemplate,
        tableId: state.context?.tableId ?? '',
        recordId: state.contractRecordId,
        fieldMetas,
      });
      if (r === 'blocked') message.warning('浏览器拦截了新标签页，已改用面板内全屏');
      else if (r === 'failed') message.warning('准备全屏数据失败，先用面板内全屏');
      if (r !== 'opened') openInPanel();
    } finally {
      setOpeningTab(false);
    }
  };
  const closeFullscreen = () => {
    setFullscreen(false);
    void setHostContainerLarge(false);
  };
  useEffect(() => {
    // 卸载/切模板时确保还原容器尺寸
    return () => {
      void setHostContainerLarge(false);
    };
  }, []);

  const commitRename = () => {
    const next = nameDraft.trim();
    if (!next || next === name) {
      setEditingName(false);
      return;
    }
    if (!editingId) return;
    if (isContract) contractTpl.update(editingId, { name: next });
    else tableTpl.rename(editingId, next);
    setEditingName(false);
  };

  const onBack = () => dispatch({ type: 'NAV', view: 'home' });

  const onDelete = () => {
    if (!editingId) return;
    Modal.confirm({
      title: '删除此模板？',
      content: '此操作无法撤销。',
      okText: '删除',
      okButtonProps: { danger: true },
      onOk: () => {
        if (isContract) contractTpl.remove(editingId);
        else tableTpl.remove(editingId);
        dispatch({ type: 'NAV', view: 'home' });
      },
    });
  };

  const onSectionsChange = (sections: typeof contractTemplate extends infer T ? T extends { sections: infer S } ? S : never : never) => {
    if (editingId && isContract) {
      contractTpl.update(editingId, { sections: sections as Parameters<typeof contractTpl.update>[1]['sections'] });
    }
  };

  if (!hasTemplate) {
    return (
      <div
        style={{
          flex: 1,
          padding: 32,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 12,
        }}
      >
        <Empty description="模板已不存在" />
        <Button onClick={onBack}>返回</Button>
      </div>
    );
  }

  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        background: 'var(--surface-1)',
      }}
    >
      {fullscreen && isContract && contractTemplate && (
        <FullscreenOverlay
          context={state.context}
          recordId={state.contractRecordId}
          template={contractTemplate}
          fieldMetas={fieldMetas}
          tableName={contractTemplate?.name || undefined}
          onUpdate={(patch) => editingId && contractTpl.update(editingId, patch)}
          onClose={closeFullscreen}
        />
      )}

      <Modal
        open={insertAt !== null}
        onCancel={() => setInsertAt(null)}
        title={insertAt?.parent ? `在第 ${insertAt.parent.col + 1} 栏里插入区块` : `在第 ${(insertAt?.index ?? 0) + 1} 个位置插入区块`}
        footer={null}
        width={360}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {INSERTABLE_SECTION_TYPES.map((type) => (
            <Button key={type} size="small" onClick={() => insertSectionAt(type)}>
              {SECTION_TYPE_LABELS[type]}
            </Button>
          ))}
        </div>
      </Modal>

      {/* Header */}
      <div
        style={{
          padding: '10px 12px',
          background: 'var(--surface-1)',
          borderBottom: '1px solid var(--border)',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <Tooltip title="返回">
          <Button shape="circle" type="text" icon={<LeftOutlined />} onClick={onBack} />
        </Tooltip>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span
              style={{
                width: 22,
                height: 22,
                borderRadius: 5,
                background: 'var(--accent)',
                color: 'var(--accent-ink)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                fontSize: 12,
              }}
            >
              {isContract ? <BookOutlined /> : <AppstoreOutlined />}
            </span>
            {editingName ? (
              <Input
                autoFocus
                value={nameDraft}
                onChange={(e) => setNameDraft(e.target.value)}
                onBlur={commitRename}
                onPressEnter={commitRename}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    setNameDraft(name || '');
                    setEditingName(false);
                  }
                }}
                size="small"
                style={{ flex: 1, fontWeight: 600, fontSize: 14 }}
                maxLength={40}
              />
            ) : (
              <button
                className="pp-bare-button"
                onClick={() => setEditingName(true)}
                style={{
                  flex: 1,
                  minWidth: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                <span
                  style={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: 'var(--ink)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  {name}
                </span>
                <EditOutlined style={{ color: 'var(--ink-4)', fontSize: 11 }} />
              </button>
            )}
          </div>
        </div>
        <Tag
          icon={<CheckCircleOutlined />}
          style={{
            background: 'var(--surface-2)',
            color: 'var(--ink-3)',
            border: 0,
            fontFamily: 'var(--font-mono)',
            fontSize: 10.5,
            margin: 0,
          }}
        >
          自动保存
        </Tag>
        {isContract && contractTemplate && (
          <ShareToggle template={contractTemplate} onUpdate={(patch) => editingId && contractTpl.update(editingId, patch)} compact />
        )}
        {isContract && (
          <>
            <Tooltip title="撤销 ⌘Z">
              <Button size="small" icon={<UndoOutlined />} disabled={!history.canUndo} onClick={history.undo} />
            </Tooltip>
            <Tooltip title="重做 ⇧⌘Z">
              <Button size="small" icon={<RedoOutlined />} disabled={!history.canRedo} onClick={history.redo} />
            </Tooltip>
          </>
        )}
        {isContract && (
          <Tooltip title="全屏编辑（三栏画布）">
            <Button
              size="small"
              type="primary"
              ghost
              icon={<ExpandOutlined />}
              loading={openingTab}
              onClick={() => void openFullscreen()}
            >
              全屏编辑
            </Button>
          </Tooltip>
        )}
        <Tooltip title="删除此模板">
          <Button
            size="small"
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={onDelete}
          />
        </Tooltip>
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }} className="pp-scroll">
        {isContract ? (
          <>
            {/* Mini preview at top（编辑态：自由元素可直接拖拽） */}
            {contractTemplate && (
              <div style={{ padding: '14px 16px 0' }}>
                {/* 对照底图：垫一张纸质单据的照片，照着它对齐标题/表格/签字位置。
                    只存本机、只在画布上出现，打印和导出都不会带上。 */}
                <Space size={6} wrap style={{ marginBottom: 8 }}>
                  <Upload
                    accept="image/*"
                    showUploadList={false}
                    beforeUpload={(f) => pickUnderlay(f as unknown as File)}
                  >
                    <Button size="small" icon={<PictureOutlined />}>
                      {underlay ? '更换底图' : '对照底图'}
                    </Button>
                  </Upload>
                  {underlay && (
                    <>
                      <Switch
                        size="small"
                        checked={underlay.visible}
                        onChange={(v) => putUnderlay({ ...underlay, visible: v })}
                      />
                      <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                        显示
                      </Typography.Text>
                      <Slider
                        min={0.1}
                        max={1}
                        step={0.05}
                        value={underlay.opacity}
                        onChange={(v) => putUnderlay({ ...underlay, opacity: v })}
                        style={{ width: 90, margin: '0 4px' }}
                        tooltip={{ formatter: (v) => `透明度 ${Math.round((v ?? 0) * 100)}%` }}
                      />
                      <Button size="small" type="text" danger onClick={() => putUnderlay(null)}>
                        清除
                      </Button>
                    </>
                  )}
                  <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                    {underlay ? '只在编辑时显示，不会印出来' : '照着纸质单据排版'}
                  </Typography.Text>
                </Space>
                <div
                  style={{
                    background: 'var(--canvas-bg)',
                    borderRadius: 12,
                    padding: 12,
                  }}
                >
                  <CanvasHints onOpenGuide={guide.show} />
                  <ContractPreview
                    context={state.context}
                    recordId={state.contractRecordId}
                    template={contractTemplate}
                    fieldMetas={fieldMetas}
                    overlayEdit
                    onOverlayPatch={(id, patch) => {
                      if (!editingId) return;
                      const overlays = (contractTemplate.overlays || []).map((o) =>
                        o.id === id ? { ...o, ...patch } : o,
                      );
                      contractTpl.update(editingId, { overlays });
                    }}
                    selectedSectionId={selectedSectionId}
                    onSectionSelect={selectFromCanvas}
                    onRebind={onRebind}
                    onSectionDuplicate={onSectionDuplicate}
                    onSectionConvert={onSectionConvert}
                    onCellSelect={setCellSel}
                    locate={locate}
                    onSectionMove={moveSectionById}
                    onSectionDelete={deleteSectionById}
                    onSectionTextChange={setSectionTextById}
                    onSectionInsert={(i, parent) => setInsertAt({ index: i, parent: parent ?? null })}
                    onSectionReorder={reorderSection}
                    onTableOp={onTableOp}
                    onUndo={history.undo}
                    onRedo={history.redo}
                    onCellText={onCellText}
                    onInsertAfter={onInsertAfter}
                    underlay={
                      underlay && underlay.visible
                        ? { dataUrl: underlay.dataUrl, opacity: underlay.opacity }
                        : null
                    }
                  />
                </div>
              </div>
            )}

            {bindingReport && (bindingReport.bound > 0 || bindingReport.unmatched.length > 0) && (
              <div style={{ padding: '0 16px', marginTop: 10 }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    flexWrap: 'wrap',
                    fontSize: 12,
                    padding: '8px 10px',
                    borderRadius: 8,
                    border: `1px solid ${bindingReport.unmatched.length ? '#e8c9b8' : 'var(--border-soft)'}`,
                    background: bindingReport.unmatched.length ? '#fdf4ef' : 'var(--surface-2)',
                  }}
                >
                  <span style={{ color: '#2d6a4f' }}>● 已绑定 {bindingReport.bound}</span>
                  <span style={{ color: bindingReport.unmatched.length ? 'var(--accent)' : 'var(--ink-3)' }}>
                    ● 未匹配 {bindingReport.unmatched.length}
                  </span>
                  <span style={{ flex: 1 }} />
                  {bindingReport.unmatched.length > 0 && (
                    <Button size="small" onClick={() => setShowUnmatched((v) => !v)}>
                      {showUnmatched ? '收起' : '看看是哪些'}
                    </Button>
                  )}
                </div>
                {showUnmatched && bindingReport.unmatched.length > 0 && (
                  <div
                    style={{
                      marginTop: 6,
                      fontSize: 11.5,
                      color: 'var(--ink-2)',
                      lineHeight: 1.8,
                      padding: '8px 10px',
                      borderRadius: 8,
                      background: 'var(--surface-2)',
                    }}
                  >
                    这些引用在当前表里找不到同名字段，打印时会是空白：
                    {bindingReport.unmatched.map((b, i) => (
                      <div key={`${b.sectionId}-${b.ref}-${i}`}>
                        <a
                          onClick={() => {
                            selectFromCanvas(b.sectionId);
                            setLocate({ id: b.sectionId, n: Date.now() });
                          }}
                          style={{ color: 'var(--accent)', cursor: 'pointer' }}
                        >
                          {b.where}
                        </a>
                        {' → '}
                        <code>{b.ref}</code>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <NumberedSection
              num="01"
              label="区块"
              count={`${contractTemplate?.sections.length ?? 0} 个`}
            >
              {contractTemplate && cellSel && (() => {
                const sec = findSectionDeep(contractTemplate.sections, cellSel.id);
                return sec && sec.type === 'free_table' ? (
                  <CellStylePanel section={sec as FreeTableSection} rect={cellSel} onPatch={onCellPatch} />
                ) : null;
              })()}
              {contractTemplate && (
                <SectionEditor
                  sections={contractTemplate.sections}
                  onChange={(sections) => onSectionsChange(sections as never)}
                  fieldMetas={fieldMetas}
                  activeId={selectedSectionId}
                  onActiveChange={setActiveSection}
                  visibility={contractTemplate.visibility}
                  onVisibilityChange={(visibility) => editingId && contractTpl.update(editingId, { visibility })}
                />
              )}
            </NumberedSection>

            <NumberedSection
              num="02"
              label="印章 · 签名区域"
              count={`${contractTemplate?.overlays?.length ?? 0} 个`}
            >
              {contractTemplate && (
                <OverlaysEditor
                  overlays={contractTemplate.overlays || []}
                  sections={contractTemplate.sections}
                  onSectionsChange={(sections) => onSectionsChange(sections as never)}
                  onChange={(overlays: OverlayItem[]) =>
                    editingId && contractTpl.update(editingId, { overlays })
                  }
                />
              )}
            </NumberedSection>

            <NumberedSection num="03" label="排版" defaultOpen={false}>
              {contractTemplate && (
                <ContractSettings
                  settings={contractTemplate.settings}
                  onChange={(settings) =>
                    editingId && contractTpl.update(editingId, { settings })
                  }
                />
              )}
            </NumberedSection>
          </>
        ) : (
          <>
            {/* 编辑即所见：视图模板也在编辑页直接预览/打印/导出，不用返回主页 */}
            <div style={{ padding: '14px 16px 0' }}>
              <div
                style={{
                  background: 'var(--canvas-bg)',
                  borderRadius: 12,
                  padding: 12,
                }}
              >
                <PreviewPane fieldMetas={fieldMetas} />
              </div>
            </div>

            <NumberedSection
              num="01"
              label="字段"
              count={`${state.fieldOptions.filter((f) => f.checked).length}/${state.fieldOptions.length}`}
            >
              {bootstrapLoading ? (
                <Spin size="small" />
              ) : (
                <FieldPanel visibleFieldIds={visibleFieldIds} />
              )}
            </NumberedSection>

            <NumberedSection num="02" label="排版" defaultOpen={false}>
              <PrintSettings />
            </NumberedSection>
          </>
        )}

        <div
          style={{
            margin: '12px 16px 16px',
            padding: '10px 12px',
            fontSize: 11.5,
            color: 'var(--ink-3)',
            background: 'var(--surface-2)',
            borderRadius: 8,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 8,
          }}
        >
          <span style={{ flexShrink: 0, marginTop: 1, color: 'var(--accent)' }}>
            <CheckCircleOutlined />
          </span>
          <span>
            修改实时保存到模板，上方预览区可直接打印 / 导出，无需返回主页。
          </span>
        </div>
      </div>
      <EditorGuide open={guide.open} onClose={guide.close} />
    </div>
  );
}
